import { useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import { dataTestId } from 'modules/experiments/constants';
import {
  useVariantCardContext,
  useVariantConfigurationContext,
} from 'modules/experiments/contexts';
import { useExperimentsTranslations } from 'modules/experiments/hooks';
import {
  FormFieldProps,
  VariantFormField,
  VariantFormFieldKey,
  VariantFormFieldType,
} from 'modules/experiments/types';
import {
  StyledVariantFormField,
  VariantPlaceholderText,
} from 'modules/experiments/components/pages/edit/sections/variant_configuration/CommonStyled';
import { useTemplateTakeover } from 'modules/templateEditor/integrations/InStudioTemplateEditing/context';
import { WebLink } from 'ui/theme';
import { ComboFormField, TextFormField } from './fields';

export const FormField = ({ formField }: FormFieldProps<VariantFormField>) => {
  const { t } = useExperimentsTranslations();
  const takeover = useTemplateTakeover();
  const { cardIndex, isCardInEditMode } = useVariantCardContext();
  const { updateSelectedVariantContent } = useVariantConfigurationContext();
  const { watch } = useFormContext();

  const getSTOLabels = () =>
    cardIndex === 0
      ? {
          label: t('variantCard.fieldPlaceholderText.controlSTO'),
          dataTest: dataTestId.variantCardBodySTOControl,
        }
      : {
          label: t('variantCard.fieldPlaceholderText.STO'),
          dataTest: dataTestId.variantCardBodySTONonControl,
        };

  /** updating the template preview section as the variant form updates */
  useEffect(() => {
    watch((values, { name = '' }) => {
      if (name) {
        updateSelectedVariantContent(name as VariantFormFieldKey, values[name]);
      }
    });
  }, [watch]);

  switch (formField.type) {
    case VariantFormFieldType.TEXT: {
      return <TextFormField formField={formField} />;
    }

    case VariantFormFieldType.COMBO: {
      return <ComboFormField formField={formField} />;
    }

    case VariantFormFieldType.NON_EDITABLE:
      return (
        <StyledVariantFormField
          labelText={formField.label}
          data-test={dataTestId.variantCardBodyNonEditable}
        >
          {isCardInEditMode ? (
            <WebLink
              data-test={dataTestId.variantCardBodyUseEditor}
              onClick={() => {
                takeover.open(takeover.viewOptions.settings);
              }}
            >
              {t('variantCard.fieldPlaceholderText.useEditorEdit')}
            </WebLink>
          ) : (
            <VariantPlaceholderText data-test={dataTestId.variantCardBodyUseEditor}>
              {t('variantCard.fieldPlaceholderText.useEditorView')}
            </VariantPlaceholderText>
          )}
        </StyledVariantFormField>
      );

    case VariantFormFieldType.STO: {
      const { label, dataTest } = getSTOLabels();

      return <VariantPlaceholderText data-test={dataTest}>{label}</VariantPlaceholderText>;
    }

    default:
      return <></>;
  }
};
